import { clearAccessToken } from '../auth'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { createContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LoadingScreen from '../components/LoadingScreen'
import ReactFilter from '../components/inputs/ReactFilter'
import * as apiStatusCodes from '../constants/apiStatusCodes'
import { canManageUsersApp } from '../global_context/capabilities/userAppCapabilities'
import Pagination from '../layout/Pagination'
import SearchInput from '../layout/SearchInput'
import usePagination from '../layout/usePagination'
import UsersIndexView from './UsersIndexView'
import * as apiUserService from './services/apiUserService'

export const UbiquaAppUserData = createContext()
export const UbiquaAppUserConsumer = UbiquaAppUserData.Consumer

const UsersIndexApp = () => {
  const navigate = useNavigate()
  const myRef = useRef(null)

  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: 25,
    current_page: 1,
    total_pages: 1
  })

  const [title, setTitle] = useState('Usuarios')
  const [isFetching, setIsFetching] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [data, setData] = useState([])
  const [query, setQuery] = useState('')
  const [filters, setFilters] = useState([
    { key: 'Roles', value: [] },
    { key: 'ACTIVO', value: ['Sí', 'No'] }
  ])
  const [filter, setFilter] = useState('')
  const [active, setActive] = useState('')
  const [version, setVersion] = useState(0)
  const [isAnyFilterSelected, setIsAnyFilterSelected] = useState(false)
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [failedCreationState, setFailedCreationState] = useState(false)
  const [failedCreationInfo, setFailedCreationInfo] = useState(null)

  useEffect(() => {
    initializeState()
    fetchFeatureRoles().then(() => fetchDataFromServer())
  }, [])

  useEffect(() => {
    fetchDataFromServer(pagination.current_page)
  }, [pagination.current_page])

  useEffect(() => {
    buildFiltersQuery()
  }, [allSelectedOptions])

  useEffect(() => {
    if (filter !== '' || active !== '') {
      fetchDataFromServer(1)
    }
  }, [filter, active])

  useEffect(() => {
    fetchDataFromServer(1)
  }, [query])

  const initializeState = () => {
    const url = window.location.href
    const queryValue =
      url.includes('query')
        ? decodeURI(url.split('query=')[url.split('query=').length - 1])
        : ''
    const pageValue =
      url.includes('pagina')
        ? parseInt(url.split('pagina=')[url.split('pagina=')[url.split('pagina=').length - 1].split('&')[0]])
        : 1

    setQuery(queryValue)
    setPagination((prev) => ({ ...prev, current_page: pageValue }))
  }

  const fetchDataFromServer = async (
    page = 1,
    queryOverride = query,
    filterOverride = filter,
    activeOverride = active
  ) => {
    setIsFetching(true)
    try {
      const response = await apiUserService
        .getAllUsers(queryOverride, page, pagination.per_page, filterOverride, activeOverride)
        .then(apiUserService.handleErrors)
      const dataJson = await response.json()

      setData(dataJson?.table || [])
      setPagination((prev) => ({
        ...prev,
        total_pages: Math.ceil(dataJson.count / prev.per_page)
      }))
      setTitle(queryOverride ? `Resultados de la búsqueda: ${queryOverride}` : 'Usuarios')
    } catch (error) {
      console.error(error)
      if (error.status === apiStatusCodes.UNAUTHORIZED) {
        clearAccessToken()
        window.location.href = '/'
      }
    } finally {
      setIsFetching(false)
      setInitialLoad(false)
    }
  }

  const fetchFeatureRoles = async () => {
    try {
      const response = await apiUserService
        .getFeatureRoles(query)
        .then(apiUserService.handleErrors)
      const dataJson = await response.json()
      prepareFilterData(dataJson?.table || [])
      return true
    } catch (error) {
      console.error(error)
      if (error.status === apiStatusCodes.UNAUTHORIZED) {
        clearAccessToken()
        window.location.href = '/'
      }
    }
  }

  const prepareFilterData = (roles) => {
    const labelMap = {}
    const roleOptions = roles.map((role) => {
      labelMap[role.name] = role.key
      return role.name
    })

    const statusOptions = [
      { name: 'Sí', value: true },
      { name: 'No', value: false }
    ]
    statusOptions.forEach((opt) => (labelMap[opt.name] = opt.value))

    setLabelToIdMapping(labelMap)
    setFilters([
      { key: 'Roles', value: roleOptions },
      { key: 'ACTIVO', value: statusOptions.map((o) => o.name) }
    ])
  }

  const setAllSelectedOptionsHandler = (identifier, selectedLabels) => {
    const selectedIds = selectedLabels.map((label) => labelToIdMapping[label])
    const updated = new Map(allSelectedOptions).set(identifier, selectedIds)
    const anySelected = Array.from(updated.values()).some((v) => v.length > 0)
    setAllSelectedOptions(updated)
    setIsAnyFilterSelected(anySelected)
  }

  const buildFiltersQuery = () => {
    let queryParam = ''
    let queryActive = ''

    allSelectedOptions.forEach((value, key) => {
      if (value.length !== 0) {
        if (key === 'Roles') queryParam += value.join('-')
        else if (key === 'ACTIVO') queryActive += value.join(',')
      }
    })

    setFilter(queryParam)
    setActive(queryActive)
  }

  const resetReactFilters = async () => {
    setAllSelectedOptions(new Map())
    setIsAnyFilterSelected(false)
    setVersion((v) => v + 1)
    setFilter('')
    setActive('')
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    await fetchFeatureRoles()
    fetchDataFromServer(1, query, '', '')
  }

  const handleSearch = (newQuery) => {
    setQuery(newQuery)
  }

  const handleCreateNewUser = () => {
    navigate('/crear_usuario_configuracion')
  }

  if (initialLoad && isFetching) {
    return <LoadingScreen text="Cargando ..."/>
  }


  return (
    <div>
      <div>
        <UbiquaAppUserData.Provider
          value={{
            per_page: pagination.per_page,
            current_page: pagination.current_page,
            total_pages: pagination.total_pages,
            query,
            filters,
            version,
            data
          }}
        >
          <UbiquaAppUserConsumer>
            {() => (
              <>
                {failedCreationState ? (
                  <div className="alert alert-danger" role="alert">
                    <strong>{failedCreationInfo}</strong>
                  </div>
                ) : (
                  <div ref={myRef}>
                    <div className="index-header detailed-container">
                      <h2>{title}</h2>
                      <div className="index-buttons">
                        {canManageUsersApp() && (
                          <button onClick={handleCreateNewUser}>Crear usuario nuevo</button>
                        )}
                      </div>
                    </div>

                    <div className="index-table">
                      <div className="show-area">
                        <h4 className="text-sub-title">Filtrar roles por:</h4>

                        <div className="filters-search-container mb-search-user">
                          <FontAwesomeIcon className="filter-icon mr-10" icon={faFilter} />
                          <div className="filters-container">
                            {filters.map((filter, indx) => (
                              <div key={`flr-${indx}`} className="mr-10">
                                <ReactFilter
                                  title={filter.key}
                                  identifier={filter.key}
                                  options={filter.value}
                                  resetWatcher={version}
                                  disabled={isFetching || filter.value.length === 0}
                                  onChange={(e) =>
                                    setAllSelectedOptionsHandler(e.identifier, e.selectedOptions)
                                  }
                                />
                              </div>
                            ))}
                          </div>

                          <div className="search-input-containter">
                            <SearchInput
                              query={query}
                              onSearch={handleSearch}
                              placeholder="Buscar usuarios por nombre o código"
                            />
                          </div>
                        </div>

                        {isAnyFilterSelected && (
                          <div className="mt-reset-filter-user">
                            <a onClick={resetReactFilters} className="reset-filter cursor-pointer">
                              Restablecer filtros aplicados
                            </a>
                          </div>
                        )}

                        <br />
                        <UsersIndexView />

                        {pagination.total_pages > 1 && (
                          <nav className="index-table" onClick={handlePaginationClick}>
                            <Pagination
                              current_page={pagination.current_page}
                              total_pages={pagination.total_pages}
                            />
                          </nav>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </UbiquaAppUserConsumer>
        </UbiquaAppUserData.Provider>
      </div>
    </div>
  )
}

export default UsersIndexApp
